import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Moon, Sun, Heart, Thermometer, Brain, Bed } from 'lucide-react';
import { calculateLunarPhase, LunarPhaseData } from '@/utils/lunarPhases';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface LunarEnergyTrackerProps {
  menopauseStage: 'perimenopause' | 'menopause' | 'postmenopause';
  onSubmit: (data: any) => void;
}

const LUNAR_ENERGY_PHASES = {
  new_moon: {
    name: 'New Moon Energy',
    icon: 'new-moon',
    energy: 'Menstruation Equivalent',
    description: 'Time for rest, reflection, and setting intentions',
    qualities: ['Introspective', 'Restful', 'Intuitive', 'Planning'],
    selfCare: ['Gentle movement', 'Meditation', 'Journaling', 'Early bedtime']
  },
  waxing_crescent: {
    name: 'Waxing Crescent Energy',
    icon: 'waxing-crescent',
    energy: 'Early Follicular Equivalent',
    description: 'Energy building, new beginnings taking shape',
    qualities: ['Growing energy', 'Optimistic', 'Creative', 'Motivated'],
    selfCare: ['Light exercise', 'Planning', 'Learning', 'Social connection']
  },
  first_quarter: {
    name: 'First Quarter Energy',
    icon: 'first-quarter',
    energy: 'Late Follicular Equivalent',
    description: 'Taking action, overcoming challenges',
    qualities: ['Decisive', 'Active', 'Problem-solving', 'Confident'],
    selfCare: ['Strength training', 'Tackle projects', 'Organise', 'Assert boundaries']
  },
  waxing_gibbous: {
    name: 'Waxing Gibbous Energy',
    icon: 'waxing-gibbous',
    energy: 'Pre-Ovulation Equivalent',
    description: 'Refining, perfecting, preparing for peak energy',
    qualities: ['Detail-oriented', 'Productive', 'Collaborative', 'Nurturing'],
    selfCare: ['Team activities', 'Cooking', 'Gardening', 'Creative projects']
  },
  full_moon: {
    name: 'Full Moon Energy',
    icon: 'full-moon',
    energy: 'Ovulation Equivalent',
    description: 'Peak energy, heightened emotions and intuition',
    qualities: ['Expressive', 'Social', 'Powerful', 'Magnetic'],
    selfCare: ['Celebrate', 'Perform', 'Lead', 'Express creativity']
  },
  waning_gibbous: {
    name: 'Waning Gibbous Energy',
    icon: 'waning-gibbous',
    energy: 'Early Luteal Equivalent',
    description: 'Sharing wisdom, giving back, nurturing others',
    qualities: ['Generous', 'Teaching', 'Grateful', 'Reflective'],
    selfCare: ['Mentor others', 'Express gratitude', 'Slow down', 'Prepare']
  },
  last_quarter: {
    name: 'Last Quarter Energy',
    icon: 'last-quarter',
    energy: 'Late Luteal Equivalent',
    description: 'Release what no longer serves, let go',
    qualities: ['Discerning', 'Honest', 'Clearing', 'Sensitive'],
    selfCare: ['Declutter', 'Say no', 'Process emotions', 'Rest more']
  },
  waning_crescent: {
    name: 'Waning Crescent Energy',
    icon: 'waning-crescent',
    energy: 'Pre-Menstrual Equivalent',
    description: 'Surrender, forgiveness, preparing for renewal',
    qualities: ['Wise', 'Forgiving', 'Peaceful', 'Intuitive'],
    selfCare: ['Forgiveness practice', 'Gentle yoga', 'Bath rituals', 'Prepare for rest']
  }
};

const MENOPAUSE_SYMPTOMS = [
  'hot_flashes', 'night_sweats', 'mood_swings', 'brain_fog', 'joint_pain', 
  'sleep_issues', 'anxiety', 'depression', 'fatigue', 'irritability',
  'weight_gain', 'bloating', 'headaches', 'dry_skin', 'low_libido'
];

export default function LunarEnergyTracker({ menopauseStage, onSubmit }: LunarEnergyTrackerProps) {
  const [currentLunarPhase, setCurrentLunarPhase] = useState<LunarPhaseData | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  
  // Energy and symptom tracking
  const [energyLevel, setEnergyLevel] = useState<number[]>([5]);
  const [moodLevel, setMoodLevel] = useState<number[]>([5]);
  const [sleepQuality, setSleepQuality] = useState<number[]>([5]);
  const [libidoLevel, setLibidoLevel] = useState<number[]>([5]);
  const [hotFlashIntensity, setHotFlashIntensity] = useState<number[]>([0]);
  const [selectedSymptoms, setSelectedSymptoms] = useState<string[]>([]);
  const [emotionalState, setEmotionalState] = useState('');
  const [notes, setNotes] = useState('');
  
  const { toast } = useToast();

  useEffect(() => {
    updateLunarPhase();
    loadExistingData();
  }, [selectedDate]);

  const updateLunarPhase = () => {
    const phase = calculateLunarPhase(selectedDate);
    setCurrentLunarPhase(phase);
  };

  const loadExistingData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const dateStr = selectedDate.toISOString().split('T')[0];
      
      const { data: existingData } = await supabase
        .from('daily_checkins')
        .select('*')
        .eq('user_id', user.id)
        .eq('date', dateStr)
        .maybeSingle();

      if (existingData) {
        // Load existing tracking data
        setEnergyLevel([existingData.energy_field ? parseInt(existingData.energy_field) : 5]);
        setMoodLevel([existingData.emotion ? parseInt(existingData.emotion) : 5]);
        setSleepQuality([existingData.sleep_quality || 5]);
        setLibidoLevel([existingData.processed_food_level || 5]); // Using available field as fallback
        setHotFlashIntensity([existingData.hot_flashes_frequency || 0]);
        setSelectedSymptoms(existingData.menopause_symptoms || []);
        setEmotionalState(existingData.body_feeling || ''); // Using available field
        setNotes(existingData.menopause_reflection || '');
      }
    } catch (error) {
      console.error('Error loading existing data:', error);
    }
  };

  const handleSymptomToggle = (symptom: string) => {
    setSelectedSymptoms(prev => 
      prev.includes(symptom) 
        ? prev.filter(s => s !== symptom)
        : [...prev, symptom]
    );
  };

  const handleSubmit = async () => {
    if (!currentLunarPhase) return;

    const trackingData = {
      date: selectedDate.toISOString().split('T')[0],
      lunar_phase: currentLunarPhase.phase,
      energy_field: energyLevel[0].toString(),
      emotion: moodLevel[0].toString(),
      sleep_quality: sleepQuality[0],
      hot_flashes_frequency: hotFlashIntensity[0],
      menopause_symptoms: selectedSymptoms,
      menopause_stage: menopauseStage,
      menopause_reflection: notes,
      body_feeling: emotionalState, // Map to available field
      // Custom tracking
      processed_food_level: libidoLevel[0], // Store libido in available numeric field
      is_perimenopausal: menopauseStage === 'perimenopause',
      is_menopausal: menopauseStage !== 'perimenopause'
    };

    try {
      await onSubmit(trackingData);
      toast({
        title: "Entry Saved",
        description: `Your ${LUNAR_ENERGY_PHASES[currentLunarPhase.phase as keyof typeof LUNAR_ENERGY_PHASES].name} tracking has been recorded.`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save your entry. Please try again.",
        variant: "destructive",
      });
    }
  };

  if (!currentLunarPhase) {
    return <div>Loading lunar data...</div>;
  }

  const currentPhaseInfo = LUNAR_ENERGY_PHASES[currentLunarPhase.phase as keyof typeof LUNAR_ENERGY_PHASES];

  return (
    <div className="space-y-6">
      {/* Current Lunar Energy Phase */}
      <Card>
        <CardHeader>
        <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Moon className="h-6 w-6" />
              Today's Energetic Phase
            </div>
            <Moon className="h-8 w-8 text-primary" />
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="text-center p-4 bg-gradient-to-r from-primary/10 to-secondary/10 rounded-lg">
              <h3 className="text-xl font-semibold mb-2">{currentPhaseInfo.name}</h3>
              <Badge variant="outline" className="mb-3">
                {currentPhaseInfo.energy}
              </Badge>
              <p className="text-sm text-muted-foreground mb-3">
                {currentPhaseInfo.description}
              </p>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <h4 className="font-medium mb-2">Qualities to embrace:</h4>
                  <ul className="space-y-1">
                    {currentPhaseInfo.qualities.map((quality, index) => (
                      <li key={index} className="text-muted-foreground">• {quality}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium mb-2">Self-care suggestions:</h4>
                  <ul className="space-y-1">
                    {currentPhaseInfo.selfCare.map((care, index) => (
                      <li key={index} className="text-muted-foreground">• {care}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tracking Inputs */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Heart className="h-5 w-5" />
            Daily Wellness Check-In
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Energy & Mood Sliders */}
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Sun className="h-4 w-4" />
                <Label>Energy Level: {energyLevel[0]}/10</Label>
              </div>
              <Slider
                value={energyLevel}
                onValueChange={setEnergyLevel}
                max={10}
                min={1}
                step={1}
                className="w-full"
              />
              <div className="text-xs text-muted-foreground">
                1 = Exhausted, 10 = Energised
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Heart className="h-4 w-4" />
                <Label>Mood: {moodLevel[0]}/10</Label>
              </div>
              <Slider
                value={moodLevel}
                onValueChange={setMoodLevel}
                max={10}
                min={1}
                step={1}
                className="w-full"
              />
              <div className="text-xs text-muted-foreground">
                1 = Very low, 10 = Excellent
              </div>
            </div>
          </div>

          {/* Sleep & Libido */}
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Bed className="h-4 w-4" />
                <Label>Sleep Quality: {sleepQuality[0]}/10</Label>
              </div>
              <Slider
                value={sleepQuality}
                onValueChange={setSleepQuality}
                max={10}
                min={1}
                step={1}
                className="w-full"
              />
            </div>

            <div className="space-y-3">
              <Label>Libido Level: {libidoLevel[0]}/10</Label>
              <Slider
                value={libidoLevel}
                onValueChange={setLibidoLevel}
                max={10}
                min={1}
                step={1}
                className="w-full"
              />
            </div>
          </div>

          {/* Hot Flash Intensity */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Thermometer className="h-4 w-4" />
              <Label>Hot Flash Intensity: {hotFlashIntensity[0]}/10</Label>
            </div>
            <Slider
              value={hotFlashIntensity}
              onValueChange={setHotFlashIntensity}
              max={10}
              min={0}
              step={1}
              className="w-full"
            />
            <div className="text-xs text-muted-foreground">
              0 = None, 10 = Severe
            </div>
          </div>

          {/* Emotional State */}
          <div className="space-y-3">
            <Label>Current Emotional State</Label>
            <Select value={emotionalState} onValueChange={setEmotionalState}>
              <SelectTrigger>
                <SelectValue placeholder="How are you feeling emotionally?" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="peaceful">Peaceful</SelectItem>
                <SelectItem value="joyful">Joyful</SelectItem>
                <SelectItem value="grateful">Grateful</SelectItem>
                <SelectItem value="anxious">Anxious</SelectItem>
                <SelectItem value="irritable">Irritable</SelectItem>
                <SelectItem value="sad">Sad</SelectItem>
                <SelectItem value="frustrated">Frustrated</SelectItem>
                <SelectItem value="overwhelmed">Overwhelmed</SelectItem>
                <SelectItem value="creative">Creative</SelectItem>
                <SelectItem value="wise">Wise</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Symptoms */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Brain className="h-4 w-4" />
              <Label>Symptoms Today</Label>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {MENOPAUSE_SYMPTOMS.map((symptom) => (
                <Button
                  key={symptom}
                  variant={selectedSymptoms.includes(symptom) ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleSymptomToggle(symptom)}
                  className="justify-start"
                >
                  {symptom.replace('_', ' ')}
                </Button>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-3">
            <Label>Reflections & Notes</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="How are you honouring this lunar energy phase? Any insights or reflections?"
              rows={3}
            />
          </div>

          <Button onClick={handleSubmit} className="w-full gap-2">
            <Moon className="h-4 w-4" />
            Save Today's Energy Check-In
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}